import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Wallet, Users, FileText, Calculator, BarChart2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "src/components/ui/tabs";
import { Button } from "src/components/ui/button";
import { RootState } from "src/redux/redux-store";
import { canSyncInvoice } from "src/pages/billing/utils/billing-utils";
import notificationService from "src/services/notification/notification.service";
import { useLoadCommissionInvoicesMutation } from "./endpoints/commissions.endpoints";
import LoadCommissionInvoicesModal from "./components/load-commission-invoices-modal";
import type { CommissionViewType, LoadCommissionInvoicesRequest } from "./types/commissions.types";
import PayoutsView from "./views/payouts-view";
import ReferralsView from "./views/referrals-view";
import InvoicesView from "./views/invoices-view";
import GrossMarginView from "./views/gross-margin-view";
import ReportsView from "./views/reports-view";

const COMMISSION_VIEWS: { id: CommissionViewType; label: string; icon: React.ElementType }[] = [
  { id: "payouts", label: "Commissions", icon: Wallet },
  { id: "referrals", label: "Referrals", icon: Users },
  { id: "invoices", label: "Paid Invoices", icon: FileText },
  { id: "gross-margin", label: "Gross margin", icon: Calculator },
  { id: "reports", label: "Reports", icon: BarChart2 },
];

const VALID_VIEW_IDS = COMMISSION_VIEWS.map((v) => v.id);
const DEFAULT_VIEW: CommissionViewType = "payouts";

const CommissionsTab: React.FC = () => {
  const dispatch = useDispatch();
  const groups = useSelector((state: RootState) => state.userState.permissions?.groups ?? []);
  const isBillingAdmin = canSyncInvoice(groups);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [loadCommissionInvoices] = useLoadCommissionInvoicesMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view") ?? DEFAULT_VIEW;
  const currentView: CommissionViewType = VALID_VIEW_IDS.includes(viewParam as CommissionViewType)
    ? (viewParam as CommissionViewType)
    : DEFAULT_VIEW;

  const setView = (viewId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("view", viewId);
      return next;
    });
  };

  const handleLoadCommissionInvoices = async(payload: LoadCommissionInvoicesRequest) => {
    try {
      const result = await loadCommissionInvoices(payload).unwrap();
      notificationService.handleSuccess(
        result,
        dispatch,
        result.message || "Commission invoices loaded successfully"
      );
    } catch (error) {
      notificationService.handleError(error, dispatch, "Failed to load commission invoices");
      throw error;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Tabs
        value={currentView}
        onValueChange={setView}
        className="flex flex-col h-full"
      >
        <div className="flex w-full items-center justify-between border-b border-gray-200">
          <TabsList className="h-auto flex-1 justify-start rounded-none border-0 bg-transparent p-0 gap-1">
            {COMMISSION_VIEWS.map(({ id, label, icon: Icon }) => (
              <TabsTrigger
                key={id}
                value={id}
                className={`gap-2 rounded-t-md rounded-b-none border-b-2 border-transparent px-4 py-3 -mb-px ${
                  currentView === id
                    ? "border-blue-500 bg-white text-gray-900 shadow-none"
                    : "bg-gray-201 text-gray-700 hover:bg-gray-200 border-transparent"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {isBillingAdmin && (
            <Button
              type="button"
              variant="black"
              size="med"
              className="shrink-0 px-6"
              onClick={() => setLoadModalOpen(true)}
            >
              Update
            </Button>
          )}
        </div>
        {isBillingAdmin && (
          <LoadCommissionInvoicesModal
            open={loadModalOpen}
            onOpenChange={setLoadModalOpen}
            onSubmit={handleLoadCommissionInvoices}
          />
        )}
        <div className="flex-1 min-h-0 overflow-auto mt-4">
          <TabsContent
            value="payouts"
            className="mt-0 h-full"
          >
            <PayoutsView />
          </TabsContent>
          <TabsContent
            value="referrals"
            className="mt-0 h-full"
          >
            <ReferralsView />
          </TabsContent>
          <TabsContent
            value="invoices"
            className="mt-0 h-full"
          >
            <InvoicesView />
          </TabsContent>
          <TabsContent
            value="gross-margin"
            className="mt-0 h-full"
          >
            <GrossMarginView />
          </TabsContent>
          <TabsContent
            value="reports"
            className="mt-0 h-full"
          >
            <ReportsView />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default CommissionsTab;
